import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FlowDefinition } from '../models/flow.model';
import { BaseService } from './base.service';
import { IBaseModel } from '../models/base.model';
import { Utils } from '../shared/utils';
import { FlowDefinitionServiceGetParams } from './interfaces/flow-definition.interface';
import { AuditLog } from '../models/auditlog.model';
import { firstValueFrom } from 'rxjs';
import { HomeStats } from '../models/home-stats.model';
import { ConfigSchemaSectionEDocsLocation } from '../models/config-schema.model';

@Injectable({
    providedIn: 'root'
})
export class FlowDefinitionService extends BaseService {
    controllerRoute = 'FlowDefinition';

    constructor(httpClient: HttpClient) {
        super(httpClient);
    }

    public async get(getParams?: FlowDefinitionServiceGetParams): Promise<IBaseModel<FlowDefinition[]>> {
        let httpParams = new HttpParams();

        httpParams = httpParams.append('flowInboxId', getParams.flowInboxId.toString());

        if (getParams?.name != null) httpParams = httpParams.append('name', getParams.name);
        if (getParams?.version != null) httpParams = httpParams.append('version', getParams.version.toString());
        if (getParams?.isActive != null) httpParams = httpParams.append('isActive', getParams.isActive.toString());
        if (getParams?.isPublished != null) httpParams = httpParams.append('isPublished', getParams.isPublished.toString());

        return await firstValueFrom(this.httpClient.get<IBaseModel<FlowDefinition[]>>(`${this.apiBaseUrl}/${this.controllerRoute}`, { params: httpParams }));
    }

    public async getById(id: string, version?: number, shouldIncludeFormSchema?: boolean): Promise<IBaseModel<FlowDefinition>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/${(shouldIncludeFormSchema ? 1 : 0)}/${version || ''}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<FlowDefinition>>(url));
    }

    public async getHistory(data: any, rowCount?: number): Promise<IBaseModel<AuditLog[]>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/GetHistory/${(rowCount || '')}`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<AuditLog[]>>(url, data));
    }

    public async getHomeStats(idUsuario: string): Promise<IBaseModel<HomeStats>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/GetHomeStats/${idUsuario}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<HomeStats>>(url));
    }

    public async insert(data: FlowDefinition): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<void>>(url, Utils.decycleFlowDefinition(data)));
    }

    public async update(data: FlowDefinition): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, Utils.decycleFlowDefinition(data)));
    }

    public async updateRoot(data: FlowDefinition): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/UpdateRoot`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, Utils.decycleFlowDefinition(data)));
    }

    public async delete(id: string): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}`;
        return await firstValueFrom(this.httpClient.delete<IBaseModel<void>>(url));
    }

    public async activate(id: string): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/Activate`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, null));
    }

    public async deactivate(id: string): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/Deactivate`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, null));
    }

    public async publish(id: string): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/Publish`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, null));
    }

    public async newVersion(id: string): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/NewVersion`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, null));
    }

    public async clone(id: string): Promise<IBaseModel<FlowDefinition>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/Clone`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<FlowDefinition>>(url, null));
    }

    public async addToContext(id: string, contextDispatcher: FlowDefinition): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/AddToContext`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, contextDispatcher));
    }

    public async removeFromContext(id: string, contextDispatcher: FlowDefinition): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/RemoveFromContext`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, contextDispatcher));
    }

    public async pin(id: string): Promise<IBaseModel<FlowDefinition>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/Pin`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<FlowDefinition>>(url, null));
    }

    public async unpin(id: string): Promise<IBaseModel<FlowDefinition>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/Unpin`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<FlowDefinition>>(url, null));
    }

    public async updateDirectory(flowId: string, directoryId?: string): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/UpdateDirectory/${flowId}/${directoryId}`;
        return await firstValueFrom(this.httpClient.patch<IBaseModel<void>>(url, {}));
    }
}
